import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useAuthorization } from 'components/Authorization';
import { useKnowledgeQueryCount } from './useKnowledgeQuery';
import { toNumber } from 'lodash-es';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { quitCreateSupplementKnowledge } from 'services/api/supplementKnowledge';
import { useGetPermission } from '../useNewPermisson';
import { useCallback, useMemo } from 'react';
import { isIdEqual } from 'utils/is';
import { useMemoizedFn } from 'ahooks';
import { useMessageList } from '../message';
import { isInQaPairMsgRang } from './useQaPairMsgRang';
import { useProductKnowledgePermission } from 'hooks/useProductKnowledgePermission';

type QAPairQuerySelectMode = 'single' | 'multiple';

interface QAPairMeta {
  visible: boolean;
  selectMode: QAPairQuerySelectMode;
  source?: SupplementKnowledge.Source;
  /** 可选范围的时间戳 */
  rangeTimestamp?: number;
  defaultKnowledgeQueryMessage?: Conversation.KnowledgeQueryMessage;
  autoClose?: boolean;
  /** 是否主动显示 */
  isActiveShow?: boolean;
}

export const QAPairQuerySelectLimit = 3;

/** 选择范围 */
export const SELECT_MSG_RANG_TIME = 1000 * 30;

/**
 * 消息的前 72小时时间戳
 */
export const getRangeTimestamp = (sendTime: number | string) => {
  return toNumber(sendTime) - 1000 * 60 * 60 * 72;
};

export const isInTimeRange = (currentTime: number | string, targetTime: number | string) => {
  const inTimeRange = toNumber(targetTime) - toNumber(currentTime) < SELECT_MSG_RANG_TIME;
  return inTimeRange;
};

const qaPairMetaState = atom<QAPairMeta>({
  visible: false,
  selectMode: 'single',
  source: undefined,
});
const qaPairVisibleState = atom<boolean>((get) => get(qaPairMetaState).visible);
const qaPairSelectModeState = atom<QAPairQuerySelectMode>((get) => get(qaPairMetaState).selectMode);

const qaPairConversationMarkInfoState = atom<Conversation.ConversationKnowledgeQuery | undefined>(
  undefined,
);

export const hasOverQuery = (
  messageList: Conversation.Message[],
  knowledgeQuery: Conversation.KnowledgeQueryMessage,
) => {
  if (messageList.length === 0) {
    return false;
  }
  return messageList.some(
    (item) =>
      isIdEqual(item.conversationId, knowledgeQuery.conversationId) &&
      isInQaPairMsgRang(item, knowledgeQuery),
  );
};

const getCustomerInitialQAPairMeta = (
  knowledgeQuery?: Conversation.KnowledgeQueryMessage,
  messageList?: Conversation.Message[],
): QAPairMeta | undefined => {
  if (knowledgeQuery && knowledgeQuery.extraInfo?.itemInfo && messageList) {
    if (hasOverQuery(messageList, knowledgeQuery)) {
      return {
        visible: true,
        source: 'CUSTOMER',
        selectMode: 'single',
        rangeTimestamp: toNumber(knowledgeQuery.sendTime),
        defaultKnowledgeQueryMessage: knowledgeQuery,
        autoClose: true,
      };
    }
  }
};

export const useQAPairConversationKnowledgeQuery = () =>
  useAtomValue(qaPairConversationMarkInfoState);
export const useSetQAPairConversationKnowledgeQuery = () =>
  useSetAtom(qaPairConversationMarkInfoState);

/** 当前会话的 QA Pair 未回复标记 */
export const useCurrentQAPairKnowledgeQuery = () => {
  const conversationKnowledgeQuery = useQAPairConversationKnowledgeQuery();
  return useMemo(
    () => conversationKnowledgeQuery?.knowledgeQuery,
    [conversationKnowledgeQuery?.knowledgeQuery],
  );
};

export const useQAPairMeta = () => useAtomValue(qaPairMetaState);
export const useSetQAPairMeta = () => useSetAtom(qaPairMetaState);

export const useQAPairVisible = () => useAtomValue(qaPairVisibleState);
export const useQAPairSelectMode = () => useAtomValue(qaPairSelectModeState);

/**
 * 补充知识主动弹窗黑名单（公司级）
 * 命中后不再自动弹出补充知识浮层，手动「提交答案」入口不受影响
 */
export const useQAPairAutoPopupDisabled = () => {
  const { whiteListConfig: { DISABLE_SUPPLEMENT_KNOWLEDGE_POPUP = false } = {} } =
    useOrgConfigStateValue();
  return DISABLE_SUPPLEMENT_KNOWLEDGE_POPUP;
};

/** 重新激活 QAPAIR 状态 */
export const useReactiveQAPair = () => {
  const qaPairOpenedWithKeepAlive = useQAPairOpenedWithKeepAlive();
  const autoPopupDisabled = useQAPairAutoPopupDisabled();
  const isTargetMessage = useIsQAPairTargetMessage();
  const messageList = useMessageList();
  const qaPairVisible = useQAPairVisible();
  const setQAPairMeta = useSetQAPairMeta();
  const { getKnowledgeQueryCount } = useKnowledgeQueryCount();
  const { payload } = useAuthorization<Oversea.Token.Payload>();

  return useMemoizedFn(
    async (conversationId: string, knowledgeQuery?: Conversation.KnowledgeQueryMessage) => {
      if (!qaPairOpenedWithKeepAlive || autoPopupDisabled || qaPairVisible || !knowledgeQuery) {
        return;
      }
      const queryMsgId = knowledgeQuery.msgId;
      if (
        !knowledgeQuery.extraInfo?.itemInfo ||
        !isIdEqual(knowledgeQuery.conversationId, conversationId)
      ) {
        return;
      }
      const messages = messageList.filter(
        (item) =>
          isIdEqual(item.conversationId, conversationId) &&
          isTargetMessage(item) &&
          isInQaPairMsgRang(item, knowledgeQuery),
      );
      if (messages.length === 0) {
        return;
      }
      const lastMessage = messages[messages.length - 1];
      if (!lastMessage) {
        return;
      }
      const filteredMessages = messages.filter((item) =>
        isInTimeRange(item.sendTime, lastMessage.sendTime),
      );
      if (filteredMessages.length === 0) {
        return;
      }

      const defaultValue = getCustomerInitialQAPairMeta(knowledgeQuery, filteredMessages);

      if (!defaultValue) {
        return;
      }

      setQAPairMeta({
        ...defaultValue,
        isActiveShow: true,
      });

      const impressions = await getKnowledgeQueryCount(conversationId);
      quitCreateSupplementKnowledge({
        query_msg_id: queryMsgId,
        assistant_id: payload?.id,
        popup_times: impressions,
        timestamp: Date.now(),
        trigger_popup: true,
        trigger_popup_msg: JSON.stringify(lastMessage),
        extra: {
          persist: true,
        },
      });
    },
  );
};

export const QAPairSupportMessageTypes: Conversation.Message['messageType'][] = [
  'TEXT',
  'IMAGE',
  'VIDEO',
];

export const useIsQAPairTargetMessage = () => {
  const { payload } = useAuthorization<Oversea.Token.Payload>();
  return useCallback(
    (msg: Conversation.Message) => {
      const { messageType, fromId } = msg;
      const sameUser = payload?.id ? `${fromId}` === `${payload?.id}` : false;
      return QAPairSupportMessageTypes.includes(messageType) && sameUser;
    },
    [payload?.id],
  );
};

export const useQAPairOpened = () => {
  const {
    whiteListConfig: { ENABLE_LECHAT_AI = false, ENABLE_SUPPLEMENT_KNOWLEDGE = false } = {},
  } = useOrgConfigStateValue();

  return ENABLE_LECHAT_AI && ENABLE_SUPPLEMENT_KNOWLEDGE;
};

export const useQAPairAICanNotReply = () => {
  const qAPairOpened = useQAPairOpened();
  const getPermission = useGetPermission();

  return useMemo(
    () => qAPairOpened && getPermission('提交AI无法回复问题'),
    [qAPairOpened, getPermission],
  );
};

export const useQAPairAIReplyError = () => {
  const qAPairOpened = useQAPairOpened();
  const getPermission = useGetPermission();

  return useMemo(
    () => qAPairOpened && getPermission('提交AI回复错误问题'),
    [qAPairOpened, getPermission],
  );
};

export const useModifyLLMKnowledge = () => {
  const qAPairOpened = useQAPairOpened();
  const productKnowledgePermission = useProductKnowledgePermission();

  return useMemo(
    () => qAPairOpened && productKnowledgePermission,
    [qAPairOpened, productKnowledgePermission],
  );
};

/**
 * 主管视角权限
 */
export const useQAPairManagerPermission = () => {
  const qAPairOpened = useQAPairOpened();
  const getPermission = useGetPermission();

  return useMemo(() => qAPairOpened && getPermission('AI智能接待'), [qAPairOpened, getPermission]);
};

/**
 * 白名单用户开启QAPAIR且开启KeepAlive
 */
export const useQAPairOpenedWithKeepAlive = () => {
  const { whiteListConfig: { QAPAIR_KEEPALIVE = false } = {} } = useOrgConfigStateValue();
  const aiCanNotReply = useQAPairAICanNotReply();
  return useMemo(() => aiCanNotReply && QAPAIR_KEEPALIVE, [aiCanNotReply, QAPAIR_KEEPALIVE]);
};

export const useGetKnowledgeReplyByMsgId = () => {
  const conversationKnowledgeQuery = useQAPairConversationKnowledgeQuery();

  return useMemoizedFn((msgId: string) => {
    if (!conversationKnowledgeQuery) {
      return;
    }
    const { knowledgeReplies } = conversationKnowledgeQuery;
    if (!knowledgeReplies) {
      return;
    }
    return knowledgeReplies[msgId];
  });
};

export const useGetKnowledgeQueryByMsgId = () => {
  const conversationKnowledgeQuery = useQAPairConversationKnowledgeQuery();

  return useMemoizedFn((msgId: string) => {
    if (!conversationKnowledgeQuery) {
      return;
    }
    const { knowledgeQueries } = conversationKnowledgeQuery;
    if (!knowledgeQueries) {
      return;
    }
    return knowledgeQueries.find((item) => isIdEqual(item.msgId, msgId));
  });
};
